import { AppColors } from "@/styles/colors";
import {
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  GridItem,
  Portal,
  Text,
} from "@chakra-ui/react";
import { InputField } from "@/components/input-field/InputField";
import { Dispatch, SetStateAction, useEffect, useMemo } from "react";
import { LightMode } from "@/components/ui/color-mode";
import { HADifferenceMotives } from "@/constants/enums";
import { onlyNumbers } from "@/utils/reg-expressions";
import { handleTonChangeInInput, parseTonInputToNumber } from "@/utils/query-string";
import { useForm, useWatch } from "react-hook-form";

export type HADifferenceItem = {
  idMotivo: number;
  valorHectareas: number;
};

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  differenceHectares: number;
  value: HADifferenceItem[];
  onChange: (next: HADifferenceItem[]) => void;
}

const motiveEntries = Object.entries(HADifferenceMotives).filter(
  ([, v]) => typeof v === "number",
) as Array<[keyof typeof HADifferenceMotives, number]>;

const motiveLabel: Record<number, string> = {
  [HADifferenceMotives.PropiasAlquiladasATerceros]: "Propias Alquiladas a Terceros",
  [HADifferenceMotives.ConGanaderia]: "Con ganadería",
  [HADifferenceMotives.nutilizables]: "Inutilizables",
  [HADifferenceMotives.Otras]: "Otras",
};

export const HADifferenceModal = ({
  open,
  setOpen,
  differenceHectares,
  value,
  onChange,
}: Props) => {
  const { control, reset } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    motiveEntries.forEach(([, id]) => {
      const found = (value ?? []).find((x) => x.idMotivo === id);
      next[`m_${id}`] = found ? String(found.valorHectareas).replaceAll(".", ",") : "";
    });
    reset(next);
  }, [open, value]);

  const diffAbs = Math.abs(differenceHectares);

  const watchedValues = useWatch({ control });

  const sumDraft = useMemo(() => {
    return motiveEntries.reduce((acc, [, id]) => {
      const raw = (watchedValues as any)?.[`m_${id}`];
      const n = parseTonInputToNumber(raw) ?? 0;
      return acc + n;
    }, 0);
  }, [watchedValues]);

  const remaining = useMemo(() => {
    const rem = diffAbs - sumDraft;
    if (!Number.isFinite(rem)) return 0;
    return rem;
  }, [diffAbs, sumDraft]);

  const isValid = diffAbs > 0 && Math.abs(sumDraft - diffAbs) < 0.00001;

  const handleAccept = () => {
    const next = motiveEntries
      .map(([, id]) => ({
        idMotivo: id,
        valorHectareas: parseTonInputToNumber((watchedValues as any)?.[`m_${id}`]) ?? 0,
      }))
      .filter((x) => x.valorHectareas > 0);

    onChange(next);
    setOpen(false);
  };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content>
              <Dialog.Header paddingY="16px" paddingLeft="24px">
                <Dialog.Title fontWeight={600} color={AppColors.GRAY_TEXT} fontSize="18px">
                  Agregar motivo de diferencia
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px">
                <Flex flexDir="column" gap="12px">
                  <Text fontSize="14px" color={AppColors.GRAY_TEXT}>
                    Ingrese motivo de diferencia de hectareas para continuar
                  </Text>

                  <Text fontSize="14px" color={AppColors.GRAY_TEXT} fontWeight={600}>
                    Hectareas de diferencia: {diffAbs}
                  </Text>

                  <Text fontSize="14px" color={AppColors.GRAY_TEXT} fontWeight={600}>
                    Diferencia restante: {remaining}
                  </Text>

                  <Grid templateColumns="repeat(1, 1fr)" gap="12px">
                    {motiveEntries.map(([, id]) => {
                      const label = motiveLabel[id] ?? String(id);
                      return (
                        <GridItem key={id}>
                          <Flex flexDir="column" gap="6px">
                            <InputField
                              control={control as any}
                              inputName={`m_${id}`}
                              labelText={label}
                              placeholder="Ingresar"
                              minHeight="40px"
                              type="text"
                              allowedPattern={onlyNumbers}
                              onChange={handleTonChangeInInput}
                            />
                          </Flex>
                        </GridItem>
                      );
                    })}
                  </Grid>

                  {!isValid && (
                    <Text fontSize="12px" color={AppColors.ERROR_TEXT}>
                      La suma de hectáreas debe ser igual a la diferencia.
                    </Text>
                  )}
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" paddingX="16px" fontWeight={600}>
                    Cancelar
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={handleAccept}
                  disabled={!isValid}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
